import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, IconButton, useMediaQuery, Skeleton } from '@mui/material';
import { getGameDescription } from '../../services/api';
import EditIcon from '@mui/icons-material/Edit';
import DescriptionUploadDialog from './DescriptionUploadDialog';
import Expandable from '../common/Expandable';
import { isSuperuser } from '../../services/auth';
import { parseDescription, containsAnimatedImage, containsVideo, isAnimatedImage, ContentPart } from '../../utils/descriptionFormatter';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

interface GameDescriptionSectionProps {
  gameId: number;
  steamId?: string;  // Steam IDを追加
}

interface Description {
  id: number;
  description: string | null;
  story: string | null;
}

const GameDescriptionSection: React.FC<GameDescriptionSectionProps> = ({ gameId, steamId }) => {
  const isMobile = useMediaQuery('(max-width:899px)');
  const [description, setDescription] = useState<Description | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [isUserSuperuser, setIsUserSuperuser] = useState(false);
  const [animatedLoaded, setAnimatedLoaded] = useState(false);  // アニメーション画像読み込み状態を管理

  const fetchDescription = async () => {
    try {
      const response = await getGameDescription(gameId, steamId);  // steamIdを渡す
      setDescription(response.data);
    } catch (error) {
      console.error('概要の取得に失敗しました:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      fetchDescription();
      const superuser = await isSuperuser();
      setIsUserSuperuser(superuser);
    };
    init();
  }, [gameId]);

  const handleUploadComplete = () => {
    fetchDescription();
    setUploadDialogOpen(false);
  };

  const renderContent = (text: string) => {
    if (!text) return null;
    
    const parts = parseDescription(text);
    let isFirstAnimated = true;
    let isFirstVideo = true;
    
    return parts.map((part, index) => {
      if (part.type === 'video') {
        // 動画の場合
        const videoUrl = part.videoSources?.mp4 || part.videoSources?.webm || '';
        if (!videoUrl) return null;

        const shouldLoadVideoLazy = isMobile && !isFirstVideo && !animatedLoaded;
        if (!shouldLoadVideoLazy) isFirstVideo = false;

        return (
          <Box 
            key={index} 
            sx={{ 
              my: 1.5,
              position: 'relative',
              minHeight: { xs: '150px', sm: '150px' },
              '& video': {
                maxWidth: '100%',
                height: 'auto',
                borderRadius: 1,
                display: 'block'
              }
            }}
          >
            {shouldLoadVideoLazy ? (
              <Box
                sx={{
                  position: 'relative',
                  height: { xs: '150px', sm: '150px' },
                  backgroundColor: 'rgba(255, 255, 255, 0.04)',
                  borderRadius: 2,
                  backgroundImage: part.poster ? `url(${part.poster})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    fontSize: '0.7rem',
                    textAlign: 'center',
                    borderRadius: 1,
                    px: 2,
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  <PlayArrowIcon sx={{ fontSize: '1rem' }} />
                  動画を読み込む
                </Box>
              </Box>
            ) : (
              <>
                <video
                  src={videoUrl}
                  poster={part.poster}
                  autoPlay
                  loop
                  muted
                  playsInline
                  style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: 4
                  }}
                />
              </>
            )}
          </Box>
        );
      } else if (part.type === 'image') {
        const isAnimated = isAnimatedImage(part.content);
        const shouldLoadLazy = isMobile && isAnimated && !isFirstAnimated && !animatedLoaded;
        if (isAnimated) isFirstAnimated = false;

        return (
          <Box 
            key={index} 
            sx={{ 
              my: 1.5,
              minHeight: isAnimated ? { xs: '100px', sm: '120px' } : 'auto',
              '& img': {
                maxWidth: '100%',
                height: 'auto',
                borderRadius: 1,
                display: 'block',
                backgroundColor: 'rgba(0, 0, 0, 0.1)'
              }
            }}
          >
            {shouldLoadLazy ? (
              <Box
                sx={{
                  position: 'relative',
                  height: { xs: '120px', sm: '180px' },
                  backgroundColor: 'rgba(255, 255, 255, 0.04)',
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    fontSize: '0.7rem',
                    textAlign: 'center',
                    borderRadius: 1,
                    px: 2,
                    py: 1,
                  }}
                >
                  画像を読み込む
                </Box>
              </Box>
            ) : (
              <img src={part.content} alt="" loading="lazy" />
            )}
          </Box>
        );
      } else {
        return (
          <Typography 
            key={index}
            variant="body2" 
            component="div"
            sx={{ 
              whiteSpace: 'pre-wrap',
              color: 'text.secondary',
              lineHeight: 1.25,
              display: 'block',
              fontSize: '0.75rem',
              '& h2': {
                mt: 2,
                mb: 1,
                fontWeight: '500',
                color: '#ffffffe3',
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                borderBottom: '1px solid #e0e0e048',
                marginTop: { xs: '1rem', sm: '1rem' },
                paddingBottom: '2px',
              },
              '& h1, & h3, & h4, & h5, & h6': {
                mt: 2,
                mb: 1,
                fontWeight: '500',
                fontSize: { xs: '0.7rem', sm: '0.75rem' }
              },
              '& p': {
                my: 0.5
              },
              '& ul, & ol': {
                my: 1,
                pl: 2
              },
              '& li': {
                my: 0.25
              }
            }}
            dangerouslySetInnerHTML={{ __html: part.content }}
          />
        );
      }
    });
  };

  if (loading) {
    return (
      <Box sx={{ mt: { xs: 3, sm: 4 }, mb: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 1 
        }}>
          <Skeleton variant="text" width={80} height={32} />
        </Box>
          <Skeleton variant="text" width="100%" />
          <Skeleton variant="text" width="95%" />
          <Skeleton variant="text" width="90%" />
          <Skeleton variant="text" width="85%" />
          <Box sx={{ mt: 2 }}>
            <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 1 }} />
          </Box>
          <Box sx={{ mt: 2 }}>
            <Skeleton variant="text" width="100%" />
            <Skeleton variant="text" width="92%" />
            <Skeleton variant="text" width="88%" />
          </Box>
      </Box>
    );
  }

  if (!description?.description && !description?.story) {
    // 開発環境またはスーパーユーザーの場合に表示
    if (process.env.NODE_ENV !== 'development' && !isUserSuperuser) {
      return null;
    }

    return (
      <Box sx={{ mt: 4, mb: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 1 
        }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>概要</Typography>
          <Button
            startIcon={<EditIcon />}
            onClick={() => setUploadDialogOpen(true)}
            size="small"
          >
            追加
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary">
          このゲームの概要はまだ登録されていません。
        </Typography>
        <DescriptionUploadDialog
          open={uploadDialogOpen}
          onClose={() => setUploadDialogOpen(false)}
          gameId={gameId}
          onUploadComplete={handleUploadComplete}
          initialDescription={description}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: { xs: 3, sm: 4 }, mb: 2 }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 1 
      }}>
        <Typography variant="h6" component="h2" gutterBottom sx={{ mb: 0, fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>概要</Typography>
        {(process.env.NODE_ENV === 'development' || isUserSuperuser) && (
          <IconButton
            size="small"
            onClick={() => setUploadDialogOpen(true)}
            sx={{ color: 'text.secondary' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Expandable 
        maxHeight={
          containsVideo(description?.description) || containsVideo(description?.story) ||
          containsAnimatedImage(description?.description) || containsAnimatedImage(description?.story)
            ? (isMobile ? 400 : 600)  // 動画またはアニメーション画像が含まれている場合は高さを大きくする
            : (isMobile ? 250 : 300)  // 通常の場合は元の高さ
        } 
        expandButtonStyle="pill" 
        iconPadding={0} 
        pillTextAlign={isMobile ? "center" : "right"}
        onExpand={(isExpanded) => {
          if (isExpanded) {
            setAnimatedLoaded(true);
          }
        }}
      >
        {description.description && (
          <Box sx={{ mb: description.story ? 2 : 0 }}>
            {renderContent(description.description)}
          </Box>
        )}
        {description.story && (
          <>
            {description.description && (
              <Typography variant="subtitle2" component="h3" sx={{ mt: 2, mb: 1 }}>ストーリー</Typography>
            )}
            <Box>
              {renderContent(description.story)}
            </Box>
          </>
        )}
      </Expandable>
      <DescriptionUploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        gameId={gameId}
        onUploadComplete={handleUploadComplete}
        initialDescription={description}
      />
    </Box>
  );
};

export default GameDescriptionSection;